import React, { useEffect, useMemo, useRef, useState } from "react";
import REIStepper from "../../../components/ui/stepper";
import { useLocation, useNavigate } from "react-router-dom";
import Analysis from "./Analysis";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import { fetchAnalysisSummary, fetchMemeberDetail } from "../../../store/slice/analyserSlice";
import AnyalysisGraph from "./Graph";
import { REIButton, REIDialoge, REIProgressSpinner } from "../../../components/ui";
import SavedAnalyser from "../SaveAnalyser";
import { httpUpdate } from "../../../services/httpService";
import { ApiEndPoints } from "../../../utils/endpoints";
import { MEMBER_KEY, USER_DETAIL } from "../../../utils/constants";
import { Toast } from "primereact/toast";
import PropertySummary from "../Property Summary/PropertySummary";
import MoveToPortfolioTracker from "./Child/MoveToPortfolioTracker";
import { AnalysedPropertyProvider } from "../PropertyStepper/Child/AnalysedPropertyContext";
import { IMemberEntity } from "../../../interface/analysed";
import { IAnalysisSummaryResponse } from "../../../interface/analysisSummary";
import { exportAnalysisToExcel } from "../../../utils/excelExport";
import { fetchFormattedAddress, shouldEnrichAddress } from "../../../utils/commonUtil";

const AnalysisReportIndex: React.FC = () => {
  const location = useLocation();
  const toastRef = useRef<Toast>(null);
  const memberDetail = JSON.parse(localStorage.getItem(USER_DETAIL) || "{}");
  const { propertyAnalyserId, propertyName, MemberCountryCode, propertyAddress, countryID } = location.state || {};
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const [showMoveToPortfolioDialog, setShowMoveToPortfolioDialog] = useState<boolean>(false);
  const memeberEntity = useSelector((state: RootState) => state.analyser.memeberEntity as IMemberEntity[]);
  const reload = useSelector((state: RootState) => state.analyser.reload);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshSavedAnalyser, setRefreshSavedAnalyser] = useState<boolean>(false);
  const analysisSummary = useSelector((state: RootState) => state.analyser.analysisSummary as IAnalysisSummaryResponse);

  const baseAddress = useMemo(() => (propertyAddress || propertyName || "").trim(), [propertyAddress, propertyName]);
  const [displayAddress, setDisplayAddress] = useState<string>(baseAddress);
  const lastLookupRef = useRef<string>("");

  useEffect(() => {
    const countryMatch = baseAddress.match(/\b(AU|NZ)\b/);
    if (countryMatch) {
      const country = countryMatch[1];

      const addressWithoutCountry = baseAddress.replace(/,?\s*\(?(AU|NZ)\)?/, "").trim();
      setDisplayAddress(`${addressWithoutCountry} (${country})`);
      return;
    } else {
      setDisplayAddress(baseAddress);
    }
  }, [baseAddress]);

  useEffect(() => {
    if (!baseAddress || !shouldEnrichAddress(baseAddress)) return;

    const apiKey = process.env.GOOGLE_MAPS_API_KEY;
    if (!apiKey || lastLookupRef.current === baseAddress) return;

    lastLookupRef.current = baseAddress;

    fetchFormattedAddress(baseAddress, apiKey, countryID).then((formatted) => {
      if (formatted) {
        setDisplayAddress(formatted);
      }
    });
  }, [baseAddress, countryID]);

  useEffect(() => {
    if (propertyAnalyserId) {
      setLoading(true);
      dispatch(fetchAnalysisSummary(propertyAnalyserId)).then(() => {
        setLoading(false);
      });
    } else {
      navigate("property-analyser");
    }
  }, [propertyAnalyserId, reload]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await dispatch(fetchMemeberDetail());
      setLoading(false);
    };

    fetchData();
  }, [dispatch]);

  const resolvedAddress = displayAddress || propertyAddress;

  const steps = [
    {
      header: "Analysis report",
      content: <Analysis propertyAddress={resolvedAddress} />,
      validate: () => true,
    },
    {
      header: "Graphs",
      content: (
        <span>
          <AnyalysisGraph />
        </span>
      ),
      validate: () => true,
    },
    {
      header: "Property summary",
      content: (
        <span>
          <PropertySummary propertyAddress={resolvedAddress}></PropertySummary>
        </span>
      ),
      validate: () => true,
    },
  ];

  const triggerSavedAnalyserRefresh = () => {
    setRefreshSavedAnalyser((prev) => !prev);
  };

  const AddToSave = async () => {
    const result = await httpUpdate(`${ApiEndPoints.analyser}/PropertyAnalyserStatus/`, {
      PropertyAnalyserId: propertyAnalyserId,
      MemberId: localStorage.getItem(MEMBER_KEY),
    });
    if (result) {
      toastRef.current?.show({
        severity: "success",
        summary: "Success Message",
        detail: "Property Analyser saved successfully.",
        life: 2000,
      });
      triggerSavedAnalyserRefresh();
    } else {
      toastRef.current?.show({
        severity: "error",
        summary: "Error Message",
        detail: "Failed to save Property Analyser.",
        life: 3000,
      });
    }
  };

  const onEdit = () => {
    navigate("/analyse-property", {
      state: {
        propertyAnalyserId: propertyAnalyserId,
        medianRequestBody: undefined,
        propertyName: resolvedAddress,
      },
    });
  };

  const handleExportToExcel = async () => {
    setLoading(true);
    const result = await dispatch(fetchAnalysisSummary(propertyAnalyserId));
    const latestAnalysisSummary = result.payload as IAnalysisSummaryResponse;
    if (latestAnalysisSummary) {
      exportAnalysisToExcel(latestAnalysisSummary, resolvedAddress);
    }
    setLoading(false);
  };

  return (
    <>
      <Toast ref={toastRef} />
      <div className="flex flex-row w-full items-start justify-between mb-1">
        <div className="flex flex-col w-1/2 gap-2 pr-2">
          <div className="flex w-full border border-x-0 border-solid border-gray-300 py-2">
            <label className="text-lg font-semibold text-blue-600">Results for {resolvedAddress}</label>
          </div>
          <div className="flex gap-1">
            <REIButton id="arpt-btn-addtosave" label="Add to save" disabled={false} loading={false} icon="" onClick={AddToSave} />
            <REIButton
              id="arpt-btn-movetoportfolio"
              label="Move to portfolio tracker"
              disabled={false}
              loading={false}
              icon=""
              onClick={() => {
                setShowMoveToPortfolioDialog(true);
              }}
            />
            <REIButton id="arpt-btn-edit" label="Edit" disabled={false} loading={false} icon="" onClick={onEdit} />
            <REIButton
              id="arpt-btn-exportexcel"
              icon="pi pi-file-excel"
              label="Export to Excel"
              classNames="p-button-success"
              onClick={handleExportToExcel}
              tooltip="Export to Excel"
            />
          </div>
        </div>

        <div className="flex w-1/2 justify-end border border-solid border-gray-300 rounded-md p-1 pa-stepper-header min-h-[100px]">
          <SavedAnalyser hideExtraColumns={true} externalRefreshData={refreshSavedAnalyser} />
        </div>
      </div>
      {showMoveToPortfolioDialog && !loading && (
        <REIDialoge
          header="Add a property to my portfolio"
          contentComponent={
            <AnalysedPropertyProvider>
              <MoveToPortfolioTracker
                memberEntity={memeberEntity}
                propertyAnalyserId={propertyAnalyserId}
                memberCountryCode={memberDetail.Country}
                propertyName={resolvedAddress}
              ></MoveToPortfolioTracker>
            </AnalysedPropertyProvider>
          }
          handleEditClick={() => {
            setShowMoveToPortfolioDialog(false);
          }}
          showHeader={true}
          visible={showMoveToPortfolioDialog}
        ></REIDialoge>
      )}
      <div className="flex flex-row bg-white pa-stepper-header">
        {loading && (
          <div className="w-full text-center">
            <REIProgressSpinner />
          </div>
        )}
      </div>
      <div className="flex flex-row bg-white pa-stepper-header">
        <div className={`flex-1 w:full border border-solid border-gray-300 rounded-md ${loading ? "opacity-60" : ""}`}>
          <REIStepper steps={steps} linear={true} />
        </div>
      </div>
      <div className="content-center justify-center flex m-2 align-middle gap-2">
        <span className="text-center content-center italic">What do you think of Property Analyser? </span>
        <REIButton
          id="arpt-btn-feedback"
          text
          label="Leave Feedback"
          onClick={() => {
            window.open("https://info.realestateinvestar.com.au/satisfaction-survey", "_blank");
          }}
          classNames="border-1 border-blue-200"
        ></REIButton>
      </div>
    </>
  );
};

export default AnalysisReportIndex;
